import React, { useCallback, useMemo, cloneElement, useState, useEffect } from 'react';
import type {
	ListIssueFieldTransformedUnionForCopyPaste,
	ClipboardItem,
} from '@atlassian/jira-business-fields/src/common/types.tsx';
import { useClipboard } from '@atlassian/jira-business-fields/src/controllers/clipboard/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useCellActions } from '../cell-navigation/index.tsx';
import type { ClipboardControllerProps } from './types.tsx';
import { canBeReset, setDataToClipboard, useClipboardErrorReporting } from './utils.tsx';

export const ClipboardController = ({
	children,
	issue,
	field,
	fieldId,
	isSelected,
	isEditMode,
	onKeyDown,
}: ClipboardControllerProps) => {
	const transformedField = useMemo(
		() =>
			issue.fields?.find((f) => {
				const fieldId_ = field.id === 'key' ? 'issuekey' : field.id;
				return f.fieldId === fieldId_;
			}),
		[field.id, issue.fields],
	);
	const isFieldAssociated = field.issueTypes?.includes(issue.issuetype.id ?? '');
	const transformedMissingField = isFieldAssociated
		? // when field is associated, but it's missing from the items query response, the value is null
			{ type: field.type, fieldId: field.id, value: null }
		: null;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);
	const [isCutting, setIsCutting] = useState<boolean>(false);
	const [contentToCopy, setContentToCopy] = useState<ClipboardItem | null>(null);

	const { setCopiedFromCell, clearCopiedFromCell } = useCellActions();

	const isEditable = field.fieldConfig?.isEditable ?? true;
	const canBeUnset = useMemo(() => canBeReset(field), [field]);

	const issueForCopyAndPaste = useMemo(
		() => ({ key: issue.key, issueId: issue.issueId, issueType: issue.issuetype }),
		[issue.issueId, issue.issuetype, issue.key],
	);

	const {
		renderPrepareToCopyComponent,
		renderPasteHandlerComponent,
		renderCutOnlyHandlerComponent,
	} = useClipboard(
		issueForCopyAndPaste,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(transformedField ?? transformedMissingField) as
			| ListIssueFieldTransformedUnionForCopyPaste
			| undefined,
		fieldId,
	);

	const { reportError, dismissFlag } = useClipboardErrorReporting(field);

	const onPaste = useCallback(
		(success: boolean, error?: Error) => {
			dismissFlag();
			setClipboard(null);

			if (success) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'cell pasted', {
					fieldType: field.type,
				});
			} else {
				reportError({
					action: 'paste',
					error,
				});
			}

			if (isFieldAssociated && !transformedField) {
				fireErrorAnalytics({
					meta: {
						id: 'jiraClipboardController',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: {
						message: `A field of type ${field.type} is missing in BE response`,
						fieldType: field.type,
					},
					error: new Error('jiraClipboardControllerMissingFieldError'),
				});
			}
		},
		[
			createAnalyticsEvent,
			dismissFlag,
			field.type,
			isFieldAssociated,
			reportError,
			transformedField,
		],
	);

	const onCut = useCallback(
		(success: boolean, error?: Error) => {
			dismissFlag();
			setIsCutting(false);

			if (!success) {
				reportError({
					action: 'cut',
					error,
				});
			}
		},
		[dismissFlag, reportError],
	);

	const paste = useCallback(
		(event: ClipboardEvent) => {
			if (isEditMode || !isEditable) {
				return;
			}

			const data = event.clipboardData;

			setClipboard({
				plainText: data?.getData('text/plain'),
				html: data?.getData('text/html'),
			});
		},
		[setClipboard, isEditMode, isEditable],
	);

	const copyEventListener = useCallback(
		(e: ClipboardEvent) => {
			if (!isSelected || isEditMode) {
				return;
			}

			dismissFlag();

			try {
				setDataToClipboard(e, contentToCopy);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				reportError({
					action: 'copy',
					error,
				});
				return;
			}

			e.preventDefault();
			setCopiedFromCell(field.id, issue?.issueId);

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell copied', {
				fieldType: field.type,
				hasCopied: true,
			});
		},
		[
			isSelected,
			isEditMode,
			contentToCopy,
			dismissFlag,
			setCopiedFromCell,
			field.id,
			field.type,
			issue?.issueId,
			createAnalyticsEvent,
			reportError,
		],
	);

	const cutEventListener = useCallback(
		(e: ClipboardEvent) => {
			if (!isSelected || isEditMode) {
				return;
			}

			try {
				setDataToClipboard(e, contentToCopy);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				reportError({
					action: 'cut',
					error,
				});
				return;
			}

			e.preventDefault();
			dismissFlag();
			setCopiedFromCell(field.id, issue?.issueId);

			setIsCutting(true);

			fireTrackAnalytics(createAnalyticsEvent({}), 'cell cut', {
				fieldType: field.type,
				canBeReset: canBeUnset,
				hasCopied: true,
			});
		},
		[
			isSelected,
			isEditMode,
			dismissFlag,
			setCopiedFromCell,
			field.id,
			field.type,
			issue?.issueId,
			createAnalyticsEvent,
			canBeUnset,
			contentToCopy,
			reportError,
		],
	);

	useEffect(() => {
		if (isSelected) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.addEventListener('paste', paste, true);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.addEventListener('copy', copyEventListener);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.addEventListener('cut', cutEventListener);
		} else {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('paste', paste, true);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('copy', copyEventListener);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('cut', cutEventListener);
		}

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('paste', paste, true);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('copy', copyEventListener);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('cut', cutEventListener);
		};
	}, [copyEventListener, cutEventListener, isSelected, paste]);

	return (
		<>
			{cloneElement(children, {
				onKeyDown,
			})}
			{clipboard &&
				renderPasteHandlerComponent({
					clipboard,
					onPaste,
					isEditable,
					onBeforePaste: clearCopiedFromCell,
				})}
			{isCutting && renderCutOnlyHandlerComponent({ onCut, isEditable: canBeUnset })}
			{isSelected &&
				renderPrepareToCopyComponent({
					onContentReady: setContentToCopy,
				})}
		</>
	);
};
